import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceMobile, CheckCircle } from '@phosphor-icons/react';

export default function PWAInstallButton({ compact = false }) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  if (installed) {
    if (compact) return null;
    return (
      <div className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-green-700 bg-green-50 border border-green-200`} data-testid="pwa-installed">
        <CheckCircle size={12} weight="fill" /> Installed
      </div>
    );
  }

  if (!deferredPrompt) return null;  // browser doesn't support / already shown

  const onClick = async () => {
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setInstalled(true);
      setDeferredPrompt(null);
    } catch (e) { /* ignore */ }
  };

  return (
    <button
      onClick={onClick}
      data-testid="pwa-install-btn"
      title={t('pwa.install_hint', 'Add Stockaris to your home screen for a faster, app-like experience')}
      className={`inline-flex items-center gap-1 ${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} bg-brand text-white hover:bg-brand/90 font-bold uppercase tracking-widest`}
    >
      <DeviceMobile size={compact ? 12 : 14} weight="bold" />
      {t('pwa.install_app', 'Install App')}
    </button>
  );
}
